import path from 'path';
import { mkdir, writeFile, stat } from 'fs/promises';
import Debug from '../../helpers/debug.js';
import ora from 'ora';
import axios from 'axios';
import Table from 'cli-table';
import AWS from 'aws-sdk';
import descriptions from '../../helpers/descriptions.js';
import { pr, pp, cl, themed, printJSON } from '../../helpers/print.js';
import { filterFiles, streamToString } from '../../helpers/utils.js';
import { genCredentialsOnClientApi } from '../../helpers/api.js';

const debug = Debug('commands:env:download');

const command = 'download';

const desc = 'Download Environment settings';

const builder = async (yargs) => {
  debug('builder', command);

  // hotglue environment download -e <env> -o -d downloads
  return yargs.option('downloadTo', descriptions.options['downloadTo'].config);
  // .option('overwrite', descriptions.options['overwrite'].config);
};

const handler = async (argv) => {
  debug('handler', command, argv);

  const { hg, json, apikey, env, downloadTo } = argv;
  const { clientApiBaseUri } = hg;

  const folderPrefix = path.resolve(process.cwd(), downloadTo);
  let message;
  let spinner = ora();
  try {
    // 1. get STS credentials
    message = themed(`Verifying user and authorizing`);
    !json && spinner.start(themed(`In progress: ${message}...`, 'secondary'));
    const { accessKeyId, secretAccessKey, sessionToken } = await genCredentialsOnClientApi({
      debug,
      baseUri: clientApiBaseUri,
      task: 'env-download',
      env,
      apikey,
    });
    !json && spinner.succeed(themed(`Finished: ${message}.`, 'secondary'));

    // create authenticated S3 instance
    const s3 = new AWS.S3({
      accessKeyId,
      secretAccessKey,
      sessionToken,
    });

    // 2. get list of files from AWS bucket
    message = themed(`Scanning environment ${themed(env, 'info')}`);
    !json && spinner.start(themed(`In progress: ${message}...`, 'secondary'));
    const params = {
      Bucket: env,
      Prefix: `config/`,
    };
    const { Contents: s3Files } = await s3.listObjectsV2(params).promise();
    !json && spinner.succeed(themed(`Finished: ${message}.`, 'secondary'));
    debug('s3-files', s3Files);

    // 3. filter only downloadable files
    const filesToDownload = s3Files
      ? filterFiles(
          s3Files.map((item) => item.Key),
          {
            pattern:
              '((*/requirements.txt)|(*/availableSources.json)|(*/availableTargets.json)|(*/availableConnectors.json)|(*/customTaps.json))',
          }
        )
      : [];
    if (!filesToDownload || filesToDownload.length === 0) {
      json
        ? printJSON({ status: 'success', downloadedFiles: [] })
        : spinner.warn(themed(`Warning: ${themed('Nothing to download!')}`, 'secondary'));
      return;
    }

    // 4. download environment config files
    message = themed(`Downloading to ${themed(folderPrefix, 'info')}`);
    !json && spinner.info(themed(`Info: ${message}.`, 'secondary'));
    const table = new Table({ head: ['File', 'Status'] });
    for await (const item of filesToDownload) {
      // create destination folder
      const filename = path.basename(item);
      const fullPath = path.resolve(folderPrefix, filename);
      debug('local-file', fullPath);

      // ensure destination folder exists
      await mkdir(path.dirname(fullPath), { recursive: true });
      // download s3 object into destination folder
      message = themed(`Downloading file: ${themed(filename, 'info')}`);
      !json && spinner.start(themed(`In progress: ${message}...`, 'secondary'));
      const fileStream = s3.getObject({ Bucket: env, Key: item }).createReadStream();
      await writeFile(fullPath, await streamToString(fileStream));
      !json && spinner.succeed(themed(`Finished: ${message}.`, 'secondary'));
      // debug('downloaded', env + item);
      table.push([filename, 'Downloaded']);
    }

    if (json) {
      printJSON({ status: 'success', downloadedFiles: filesToDownload });
    } else {
      // print results
      cl(table.toString());
    }
  } catch (err) {
    if (json) {
      printJSON({ status: 'error', error: err });
    } else {
      spinner.fail(themed(`Error: ${message}.`, 'secondary'));
      pr(themed(`Message: ${themed(err.message)}`, 'secondary'));
      debug(err);
      if (err && err.response && err.response.data) {
        debug('response', err.response.data);
      }
    }
    process.exit(1);
  }
};

export { command, desc, builder, handler };
